import type {
  ParameterTimeSeriesConfiguration,
  ParameterTimeSeriesThreshold,
  ParametersUnit,
} from "@/lib/api";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import {
  intersectUnitCodes,
  pickAutoUnitCode,
} from "@/lib/parameters/parameter-units.utils";
import { isValidThreshold } from "@/lib/api/parameter-time-series-threshold.utils";
import { visualizationSupportsThresholds } from "@/features/shared/widgets/model-map/model-map-visualization-types";

/** CMS draft while configuring a time-series group before unit is chosen. */
export type TimeSeriesConfiguratorDraft = {
  title: string;
  visualization_type: string;
  series: ParameterTimeSeriesConfiguration["series"];
  thresholds: ParameterTimeSeriesThreshold[];
  unit?: ParametersUnit;
};

export function createEmptyTimeSeriesConfiguratorDraft(): TimeSeriesConfiguratorDraft {
  return {
    title: "",
    visualization_type: "line_graph",
    series: [],
    thresholds: [],
  };
}

export function toTimeSeriesConfiguratorDraft(
  config: ParameterTimeSeriesConfiguration,
): TimeSeriesConfiguratorDraft {
  return {
    title: config.title,
    visualization_type: config.visualization_type,
    series: [...config.series],
    thresholds: config.thresholds ? [...config.thresholds] : [],
    unit: config.unit ?? undefined,
  };
}

export function resolveSeriesParameters(
  config: Pick<TimeSeriesConfiguratorDraft, "series">,
  parameters: TimeSeriesParameterOption[],
): TimeSeriesParameterOption[] {
  return config.series
    .map((series) => parameters.find((parameter) => parameter.id === series.parameter_id))
    .filter((parameter): parameter is TimeSeriesParameterOption => !!parameter);
}

export function resolveOverlappingUnitsForSeries(
  config: Pick<TimeSeriesConfiguratorDraft, "series">,
  parameters: TimeSeriesParameterOption[],
): ParametersUnit[] {
  return intersectUnitCodes(resolveSeriesParameters(config, parameters));
}

export function resolveConfiguratorUnitSelection(
  overlappingUnits: ParametersUnit[],
  preferredUnitCode: string | undefined,
): { unitCode?: string; unit?: ParametersUnit } {
  const unitCode = pickAutoUnitCode(overlappingUnits, preferredUnitCode);
  if (!unitCode) return {};
  const unit = overlappingUnits.find((candidate) => candidate.code === unitCode);
  return unit ? { unitCode, unit } : {};
}

/**
 * Whether the footer's confirm action may run. `requireTitle` is set by the
 * editors that render the title in their header (widget dialog, group modal):
 * there the name is a required field, so an empty one must block saving
 * instead of silently becoming the untitled label.
 */
export function isTimeSeriesDraftReadyToSave(
  draft: TimeSeriesConfiguratorDraft,
  options?: { requireTitle?: boolean },
): boolean {
  if (draft.series.length === 0 || !draft.unit?.code) return false;
  if (!draft.thresholds.every(isValidThreshold)) return false;
  if (options?.requireTitle && !draft.title.trim()) return false;
  return true;
}

export function finalizeTimeSeriesConfiguration(
  draft: TimeSeriesConfiguratorDraft,
  untitledTitle?: string,
): ParameterTimeSeriesConfiguration | null {
  if (draft.series.length === 0 || !draft.unit?.code) return null;
  // The title is optional: fall back to the untitled label when the caller
  // provides one (matches the header/preview presentation). Without a fallback
  // an empty title is still rejected.
  const title = draft.title.trim() || untitledTitle?.trim();
  if (!title) return null;
  // Only line graphs carry thresholds; drop any left over from a prior presentation.
  const thresholds = visualizationSupportsThresholds(draft.visualization_type)
    ? draft.thresholds.filter(isValidThreshold)
    : [];
  return {
    title,
    visualization_type: draft.visualization_type,
    series: draft.series,
    unit: draft.unit,
    ...(thresholds.length > 0 ? { thresholds } : {}),
  };
}

/**
 * Like {@link finalizeTimeSeriesConfiguration} but tolerant of an in-progress
 * draft: it does NOT require a title (falls back to `untitledTitle`), so a live
 * preview can render as soon as the draft has at least one series and a unit.
 * Returns `null` until then. Only valid thresholds are included.
 */
export function toPreviewTimeSeriesConfiguration(
  draft: TimeSeriesConfiguratorDraft,
  untitledTitle: string,
): ParameterTimeSeriesConfiguration | null {
  if (draft.series.length === 0 || !draft.unit?.code) return null;
  // Mirror save behavior: thresholds only apply to line graphs.
  const thresholds = visualizationSupportsThresholds(draft.visualization_type)
    ? draft.thresholds.filter(isValidThreshold)
    : [];
  return {
    title: draft.title.trim() || untitledTitle,
    visualization_type: draft.visualization_type,
    series: draft.series,
    unit: draft.unit,
    ...(thresholds.length > 0 ? { thresholds } : {}),
  };
}
